'use client';
import { useState } from 'react';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';

const QUESTION_PREFIX = /^Q:\s*/i;
const TITLE_PREFIX = /^FAQ:\s*/i;

const extractText = (node) => {
    if (!node) return '';
    if (typeof node.text === 'string') return node.text;
    if (!Array.isArray(node.content)) return '';
    return node.content.map(extractText).join('');
};

const isHeading = (node) => node?.type === 'heading';
const isQuestion = (node) => isHeading(node) && QUESTION_PREFIX.test(extractText(node).trim());
const isFaqTitle = (node) => isHeading(node) && TITLE_PREFIX.test(extractText(node).trim());

function collectItems(content, startIdx) {
    const items = [];
    let i = startIdx;
    while (i < content.length && isQuestion(content[i])) {
        const question = extractText(content[i]).replace(QUESTION_PREFIX, '').trim();
        const answer = [];
        i++;
        while (i < content.length && !isQuestion(content[i]) && !isHeading(content[i])) {
            answer.push(content[i]);
            i++;
        }
        items.push({ type: 'faqItem', attrs: { question }, content: answer });
    }
    return { items, endIdx: i };
}

export function transformFAQs(doc) {
    if (!doc || !Array.isArray(doc.content)) return doc;

    const out = [];
    let i = 0;
    while (i < doc.content.length) {
        const node = doc.content[i];

        if (isFaqTitle(node) && isQuestion(doc.content[i + 1])) {
            const title = extractText(node).replace(TITLE_PREFIX, '').trim();
            const { items, endIdx } = collectItems(doc.content, i + 1);
            out.push({ type: 'faqGroup', attrs: { title }, content: items });
            i = endIdx;
            continue;
        }

        if (isQuestion(node)) {
            const { items, endIdx } = collectItems(doc.content, i);
            out.push({ type: 'faqGroup', attrs: {}, content: items });
            i = endIdx;
            continue;
        }

        out.push(node);
        i++;
    }

    return { ...doc, content: out };
}

export function FAQGroup({ node }) {
    const [open, setOpen] = useState(0);

    const title = node?.attrs?.title;
    const items = Array.isArray(node?.content) ? node.content : [];
    if (!items.length) return null;

    const total = items.length;
    const currentIdx = open ?? 0;

    return (
        <section className="my-12">
            <div className="mb-10 flex items-end justify-between gap-6">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-fuschia-600">
                        FAQ
                    </p>
                    {title && (
                        <h2 className="mt-2 text-3xl font-semibold tracking-tight text-blue-700 sm:text-4xl">
                            {title}
                        </h2>
                    )}
                </div>
                <span className="font-mono text-xs uppercase tracking-widest text-blue-500">
                    {String(currentIdx + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                </span>
            </div>

            <div className="relative">
                <div className="absolute inset-y-0 left-0 w-px bg-blue-200" aria-hidden />
                {items.map((item, i) => {
                    const question = item?.attrs?.question;
                    const answerDoc = { type: 'doc', content: item?.content ?? [] };
                    const hasAnswer = answerDoc.content.length > 0;
                    const isOpen = open === i;

                    return (
                        <div key={i} className="relative pl-8">
                            <span
                                className={`absolute left-0 top-8 h-6 w-[3px] rounded-full transition-all duration-300 ${
                                    isOpen ? 'bg-fuschia-700' : 'bg-transparent'
                                }`}
                                aria-hidden
                            />
                            <button
                                type="button"
                                aria-expanded={isOpen}
                                onClick={() => setOpen(isOpen ? null : i)}
                                className="flex w-full cursor-pointer items-center justify-between gap-4 py-6 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fuschia-600">
                                <span className="flex items-baseline gap-4">
                                    <span className="font-mono text-[11px] text-blue-500">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <span
                                        className={`text-lg font-semibold transition-colors ${
                                            isOpen ? 'text-fuschia-700' : 'text-blue-700'
                                        }`}>
                                        {question}
                                    </span>
                                </span>
                                <span
                                    className={`text-xs uppercase tracking-widest transition-colors ${
                                        isOpen ? 'text-fuschia-700' : 'text-blue-500'
                                    }`}>
                                    {isOpen ? 'Close' : 'Open'}
                                </span>
                            </button>
                            {hasAnswer && (
                                <div
                                    className={`grid transition-all duration-300 ${
                                        isOpen
                                            ? 'grid-rows-[1fr] opacity-100'
                                            : 'grid-rows-[0fr] opacity-0'
                                    }`}>
                                    <div className="overflow-hidden">
                                        <div className="pb-8">
                                            <DotBlockEditor blocks={answerDoc} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {i < items.length - 1 && <div className="h-px bg-blue-200" />}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
